import { Avatar, Box, Button, Chip, Divider, Stack, TextField, Typography } from '@mui/material';
import { FormEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { startSocialSignIn } from './auth-api';
import { getAuthErrorCallbackUrl } from './auth-utils';
import MagicLinkButton from './MagicLinkButton';
import { betterAuthClient } from '@/libs/better-auth-client';

// ─── Component ────────────────────────────────────────────────────────────────

export default function AuthEmailStep({
    variant,
    initialEmail,
    redirectPath,
    oauthQuery,
    onContinue,
    lockEmail = false,
}: {
    variant: 'login' | 'register';
    initialEmail: string;
    redirectPath: string;
    oauthQuery?: string;
    onContinue: (email: string) => void;
    lockEmail?: boolean;
}) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [email, setEmail] = useState(initialEmail);
    const [loadingProvider, setLoadingProvider] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const lastMethod = variant === 'login' ? betterAuthClient.getLastUsedLoginMethod() : null;

    const onSubmit = (e: FormEvent) => {
        e.preventDefault();
        if (email.trim()) {
            onContinue(email.trim());
        }
    };

    const onSocial = async (provider: string) => {
        setLoadingProvider(provider);
        await startSocialSignIn(provider, redirectPath, getAuthErrorCallbackUrl(), oauthQuery, (msg) =>
            enqueueSnackbar(msg),
        );
        setLoadingProvider(null);
    };

    const isLogin = variant === 'login';

    // Invite flow: the email is fixed to the invited address (Better Auth enforces the match on accept),
    // so render it read-only and drop social sign-in (a social account's email may not match).
    if (lockEmail) {
        return (
            <Stack spacing={3}>
                {isLogin && (
                    <Box>
                        <Typography variant="h5" fontWeight={700} mb={0.5}>
                            {t('auth.login.title')}
                        </Typography>
                    </Box>
                )}

                <Stack component="form" spacing={2} onSubmit={onSubmit}>
                    <TextField
                        label={t('auth.step.email.label')}
                        type="email"
                        value={email}
                        InputProps={{ readOnly: true }}
                        autoComplete="email"
                        fullWidth
                    />
                    {isLogin ? (
                        <>
                            <Button type="submit" variant="contained" size="large" fullWidth>
                                {t('auth.step.email.signin')}
                            </Button>
                            <MagicLinkButton
                                email={email}
                                redirectPath={redirectPath}
                                oauthQuery={oauthQuery}
                                variant="outlined"
                            />
                        </>
                    ) : (
                        <Button type="submit" variant="contained" size="large" fullWidth>
                            {t('auth.step.email.continue')}
                        </Button>
                    )}
                </Stack>
            </Stack>
        );
    }

    return (
        <Stack spacing={3}>
            {isLogin && (
                <Box>
                    <Typography variant="h5" fontWeight={700} mb={0.5}>
                        {t('auth.login.title')}
                    </Typography>
                </Box>
            )}

            {/* Social providers — all 5 with visible labels */}
            <Stack spacing={1.5}>
                <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: '0.06em', lineHeight: 1 }}
                >
                    {t('auth.social.title')}
                </Typography>
                {/* Full-width outlined sign-in buttons. Instagram/TikTok are intentionally excluded —
                    they don't return an email, so they'd create emailless accounts. */}
                {(['google', 'facebook', 'linkedin'] as const).map((provider) => (
                    <Box key={provider} sx={{ position: 'relative' }}>
                        <Button
                            size="large"
                            variant="outlined"
                            fullWidth
                            startIcon={
                                <Avatar
                                    variant="square"
                                    sx={{ width: 18, height: 18 }}
                                    alt={t(`auth.social.provider.${provider}`)}
                                    src={`/social/${provider.toLowerCase()}.svg`}
                                />
                            }
                            onClick={() => onSocial(provider)}
                            disabled={loadingProvider !== null}
                        >
                            {loadingProvider === provider
                                ? t('auth.common.loading')
                                : t('auth.social.sign-in', {
                                      provider: t(`auth.social.provider.${provider}`),
                                  })}
                        </Button>
                        {lastMethod === provider && loadingProvider === null && (
                            <Chip
                                label={t('auth.last-used')}
                                size="small"
                                color="primary"
                                variant="outlined"
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    right: 12,
                                    transform: 'translateY(-50%)',
                                    height: 20,
                                    fontSize: '0.65rem',
                                    pointerEvents: 'none',
                                }}
                            />
                        )}
                    </Box>
                ))}

            </Stack>

            <Divider>
                <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                    {t('auth.common.or')}
                </Typography>
            </Divider>

            {/* Email form */}
            <Stack component="form" spacing={2} onSubmit={onSubmit}>
                <TextField
                    inputRef={inputRef}
                    label={t('auth.step.email.label')}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    autoComplete="email"
                    fullWidth
                />
                {isLogin ? (
                    <>
                        <Box sx={{ position: 'relative' }}>
                            <Button type="submit" variant="contained" size="large" fullWidth>
                                {t('auth.step.email.signin')}
                            </Button>
                            {lastMethod === 'email' && (
                                <Chip
                                    label={t('auth.last-used')}
                                    size="small"
                                    color="primary"
                                    variant="filled"
                                    sx={{
                                        position: 'absolute',
                                        top: '50%',
                                        right: 12,
                                        transform: 'translateY(-50%)',
                                        height: 20,
                                        fontSize: '0.65rem',
                                        pointerEvents: 'none',
                                        bgcolor: 'primary.contrastText',
                                        color: 'primary.main',
                                    }}
                                />
                            )}
                        </Box>
                        <MagicLinkButton
                            email={email}
                            redirectPath={redirectPath}
                            oauthQuery={oauthQuery}
                            variant="outlined"
                        />
                    </>
                ) : (
                    <Button type="submit" variant="contained" size="large" fullWidth>
                        {t('auth.step.email.continue')}
                    </Button>
                )}
            </Stack>
        </Stack>
    );
}
